import { useState, useEffect, useMemo, lazy, Suspense, useRef } from "react";
import { Avatar } from "../components/Avatar";
import { MainResultCard } from "../components/MainResultCard";
import { StatCard } from "../components/StatCard";
import { ReadinessScore } from "../components/ReadinessScore";
import { ReadinessBreakdown } from "../components/ReadinessBreakdown";
import { RecommendationCard } from "../components/RecommendationCard";
import {
  DashboardLoading,
  DashboardProfileError,
  DashboardProjectionsError,
  DashboardCalculating,
} from "../components/DashboardStatusScreens";
import { FinancialSummary } from "../components/FinancialSummary";
import { ProjectionDetailPage } from "../components/ProjectionDetailPage";
import { TrialBanner } from "../components/TrialBanner";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { WhatIfPanel } from "../components/WhatIfPanel";
import { useDashboardLogic } from "./Dashboard.logic";
import { useProjections } from "../hooks/useProjections";
import { useScenarios } from "../hooks/useScenarios";
import { useTrial } from "../hooks/useTrial";
import { useWhatIfScenario } from "../hooks/useWhatIfScenario";
import { useMetricDeltas } from "../hooks/useMetricDeltas";
import { useDashboardHandlers } from "../hooks/useDashboardHandlers";
import { ScenarioSelector } from "../components/ScenarioSelector";
import { ScenarioComparison } from "../components/ScenarioComparison";
import { UndoToastStack } from "../components/UndoToast";
import type { ScenarioPreview } from "../types/scenario";
import { generateRecommendations } from "../utils/recommendations";
import { getReadinessCopy } from "../utils/dashboardCopy";
import {
  formatCurrency,
  displayYears,
  displayPercentage,
} from "../utils/formatting";
import { Plan } from "../types/user";

const PortfolioChart = lazy(async () => {
  const module = await import("../components/PortfolioChart");
  return { default: module.PortfolioChart };
});

/**
 * Dashboard - Mobile implementation
 *
 * Mobile-specific dashboard where settings are accessed via navigation to /settings page
 */
function DashboardMobile() {
  const {
    user,
    userProfile,
    age,
    loading: authLoading,
    error: authError,
    handleSignOut,
    navigate,
  } = useDashboardLogic();

  // Trial banner state
  const trial = useTrial(userProfile?.createdAt);

  // Fetch retirement projections from Firestore
  const {
    data,
    result,
    netWorthAtRetirement,
    totals,
    assetBreakdown,
    loading: projectionsLoading,
    error: projectionsError,
  } = useProjections(user?.userId);

  // Mobile-specific menu state (no keyboard navigation needed)
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProjectionPageOpen, setIsProjectionPageOpen] = useState(false);
  const recommendationsRef = useRef<HTMLDivElement>(null);

  const basePlan = userProfile?.plan ?? Plan.fromName("basic");
  const maxScenarios = basePlan.maxScenarios;

  const {
    scenarios,
    scenariosLoading,
    scenariosError,
    activeScenarioName,
    isCurrentSettingsSaved,
    saveScenario,
    loadScenario,
    deleteScenario,
    pendingDeletes,
    undoDelete,
    confirmDelete,
  } = useScenarios(user?.userId, maxScenarios);

  // What-If scenario mode
  const whatIf = useWhatIfScenario(data);

  // Metric deltas after settings changes
  const metricDeltas = useMetricDeltas(whatIf.isActive);

  // Check for deltas when result is available (e.g., after returning from /settings)
  useEffect(() => {
    if (result) {
      metricDeltas.checkForDeltas({
        monthlyIncome: result.monthlyIncome,
        readinessScore: result.readinessScore,
        yearsToRetirement: result.yearsToRetirement,
        savingsRate: result.savingsRate ?? 0,
        incomeCoverage: result.incomeCoverage,
      });
    }
  }, [result, metricDeltas]);

  // Shared handlers (comparison, what-if save, smart defaults, save scenario)
  const {
    handleWhatIfSave,
    comparisonScenarioIds,
    openComparison,
    closeComparison,
    handleCompareViewDetails,
    handleSaveScenario,
  } = useDashboardHandlers({
    userId: user?.userId ?? "",
    whatIf,
    loadScenario,
    saveScenario,
  });

  const handleAvatarClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSettingsClick = () => {
    setIsMenuOpen(false);
    // Capture current metrics for delta comparison when returning
    if (result) {
      metricDeltas.captureBeforeSettings({
        monthlyIncome: result.monthlyIncome,
        readinessScore: result.readinessScore,
        yearsToRetirement: result.yearsToRetirement,
        savingsRate: result.savingsRate ?? 0,
        incomeCoverage: result.incomeCoverage,
      });
    }
    // Mobile: navigate to /settings page
    navigate("/settings");
  };

  const handleLoadScenario = async (scenario: { id: string; name: string }) => {
    await loadScenario(scenario.id);
  };

  const handleDeleteScenario = async (id: string) => {
    await deleteScenario(id);
  };

  // Original slider values for What-If comparison display (F-06: memoized)
  // Must be before early returns to satisfy React hooks rules
  const originalSliderValues = useMemo(
    () =>
      data
        ? {
            retirementAge: data.retirementAge,
            lifeExpectancy: data.lifeExpectancy,
            expectedReturnRate: data.annualReturn,
            inflationRate: data.inflationRate,
            expensesAtRetirement: data.expensesAtRetirement,
          }
        : undefined,
    [data]
  );

  // Show loading while auth or projections are loading
  if (authLoading || projectionsLoading) {
    return <DashboardLoading />;
  }

  // Auth and profile loading complete - check if we have profile
  if (!userProfile) {
    return (
      <DashboardProfileError error={authError} onSignOut={handleSignOut} />
    );
  }

  // Check for projections error
  if (projectionsError) {
    return (
      <DashboardProjectionsError
        error={projectionsError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Check if we have data and result before rendering dashboard
  if (!data || !result) {
    return <DashboardCalculating />;
  }

  // When scenario mode is active, use scenario values for display
  const activeData =
    whatIf.isActive && whatIf.scenarioData ? whatIf.scenarioData : data;
  const activeResult =
    whatIf.isActive && whatIf.scenarioResult ? whatIf.scenarioResult : result;

  return (
    <div className="dashboard-shell" id="dashboard-mobile">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h1 className="logo">RetireEZ</h1>
          <span className="header-page-title">Dashboard</span>

          {/* Avatar with dropdown menu */}
          <div className="avatar-dropdown-container">
            <button
              onClick={handleAvatarClick}
              className="avatar-menu-trigger"
              aria-label="User menu"
            >
              <Avatar profile={userProfile} size="small" />
            </button>

            {/* Dropdown menu - simple touch interaction, no keyboard nav */}
            {isMenuOpen && (
              <div className="user-dropdown-mobile">
                {/* User info section */}
                <div className="user-dropdown-info">
                  <div className="user-dropdown-name">
                    {userProfile.fullName()}
                  </div>
                  <div className="user-dropdown-email">{user?.email}</div>
                </div>

                {/* Settings option */}
                <button
                  onClick={handleSettingsClick}
                  className="user-dropdown-item"
                  aria-label="Settings"
                >
                  Settings
                </button>

                {/* Sign Out button */}
                <div className="dashboard-dropdown-separator">
                  <button
                    onClick={handleSignOut}
                    className="user-dropdown-item-signout"
                    aria-label="Sign out"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Trial Banner — between header and main content */}
      {trial.showBanner && (
        <TrialBanner
          variant="banner"
          trialDay={trial.trialDay}
          totalDays={trial.totalDays}
          onUpgrade={() => navigate("/pricing")}
          onDismiss={trial.handleDismiss}
          dismissible={trial.dismissible}
        />
      )}

      {/* Blocking upgrade modal when trial expired */}
      {trial.showBlockingModal && (
        <UpgradePrompt
          featureId="trial-expired"
          title="Your Free Trial Has Ended"
          description="Upgrade to continue using all RetireEZ features."
          buttonText="Compare Plans"
        />
      )}

      {/* Scenario Mode Banner — sticky below header when What-If is active */}
      {whatIf.isActive && (
        <div className="what-if-scenario-banner" id="scenario-mode-banner">
          <span>⚡ Scenario Mode — projections are temporary</span>
          <button
            type="button"
            className="what-if-scenario-banner-exit"
            onClick={whatIf.reset}
          >
            Exit
          </button>
        </div>
      )}

      {/* WP-4 Gap 4.1.1: Sticky summary bar — retains key metric context during scroll */}
      <div className="mobile-sticky-summary" id="mobile-sticky-summary">
        <span className="mobile-sticky-summary-label">Projected Net Worth</span>
        <span className="mobile-sticky-summary-value">
          {formatCurrency(
            netWorthAtRetirement ?? activeResult.finalAmount,
            activeData.currency
          )}
        </span>
      </div>

      {/* Main Content */}
      <div className="dashboard-main-content">
        {/* Dashboard Content - Mobile Optimized */}
        <div className="dashboard-content-inner">
          {/* Scenario Selector — left-aligned */}
          <div className="dashboard-header-row">
            <ScenarioSelector
              scenarios={scenarios}
              activeScenarioName={activeScenarioName}
              preview={{
                retirementAge: activeData.retirementAge,
                finalValue: netWorthAtRetirement ?? activeResult.finalAmount,
                monthlyIncome: activeResult.monthlyIncome,
                readinessScore: activeResult.readinessScore,
                shortTermHealthScore: activeResult.shortTermHealthScore,
                liquidReservesScore: activeResult.liquidReservesScore,
                cashFlowMarginScore: activeResult.cashFlowMarginScore,
                lifestyleScore: activeResult.lifestyleScore,
                currency: activeData.currency,
              }}
              onSave={handleSaveScenario}
              onLoad={handleLoadScenario}
              onDelete={handleDeleteScenario}
              onCompare={openComparison}
              isLoading={scenariosLoading}
              error={scenariosError || undefined}
              isCurrentSettingsSaved={isCurrentSettingsSaved}
              maxScenarios={maxScenarios}
              disabled={whatIf.isActive}
              disabledTooltip="Exit Scenario Mode to manage scenarios"
            />
          </div>

          {/* Hero Metric - Main Result Card */}
          <MainResultCard
            retirementAge={activeData.retirementAge}
            finalAmount={netWorthAtRetirement ?? activeResult.finalAmount}
            currency={activeData.currency}
            subtitle="Projected net worth — all assets minus liabilities at retirement"
            readinessCopy={getReadinessCopy(activeResult.readinessScore)}
            isScenarioMode={whatIf.isActive}
            onViewDetails={() => setIsProjectionPageOpen(true)}
            onSeeRecommendations={() =>
              recommendationsRef.current?.scrollIntoView({
                behavior: "smooth",
              })
            }
          />

          {/* Financial Summary Section */}
          {totals && userProfile && (
            <div className="dashboard-section">
              <div className="dashboard-section-header">
                <h2>Financial Summary</h2>
              </div>
              <FinancialSummary
                totals={totals}
                currentAge={age}
                currency={activeData.currency}
                compact
              />
            </div>
          )}

          {/* Readiness Score Gauge + Breakdown - Full Width on Mobile */}
          <div className="readiness-container">
            <div className="readiness-stack">
              <ReadinessScore score={activeResult.readinessScore} size="md" />
              <ReadinessBreakdown
                overallScore={activeResult.readinessScore}
                shortTermHealthScore={activeResult.shortTermHealthScore}
                emergencyFundScore={activeResult.liquidReservesScore}
                cashFlowScore={activeResult.cashFlowMarginScore}
                lifestyleScore={activeResult.lifestyleScore}
                emergencyFundCurrent={assetBreakdown?.emergencyFund}
                emergencyFundTarget={
                  activeData ? activeData.monthlyExpenses * 6 : undefined
                }
                monthlyCashFlowSurplus={
                  totals ? totals.income - totals.expenses : undefined
                }
                currency={activeData?.currency}
                onNavigateToSettings={(category) =>
                  navigate(`/settings/${category}`)
                }
              />
            </div>
          </div>

          {/* Stat Cards - Single Column on Mobile */}
          <div className="dashboard-stat-cards-stack">
            <StatCard
              label="Years to Retirement"
              value={displayYears(activeResult.yearsToRetirement)}
              icon="⏳"
              color="blue"
              deltaInfo={
                metricDeltas.deltas?.yearsToRetirement
                  ? {
                      delta: metricDeltas.deltas.yearsToRetirement,
                      type: "score",
                      stagger: 0,
                    }
                  : undefined
              }
            />
            <StatCard
              label="Monthly Retirement Income"
              value={formatCurrency(
                activeResult.monthlyIncome,
                activeData.currency
              )}
              subtitle={
                activeData.expensesAtRetirement === 0
                  ? undefined
                  : `${activeData.expensesAtRetirement > 0 ? "+" : ""}${displayPercentage(activeData.expensesAtRetirement)} expense change`
              }
              icon="💰"
              color="green"
              deltaInfo={
                metricDeltas.deltas?.monthlyIncome
                  ? {
                      delta: metricDeltas.deltas.monthlyIncome,
                      type: "currency",
                      stagger: 1,
                    }
                  : undefined
              }
            />
            <StatCard
              label="Savings Rate"
              value={
                activeResult.savingsRate !== undefined
                  ? displayPercentage(activeResult.savingsRate / 100)
                  : "---"
              }
              icon="📊"
              color="purple"
              deltaInfo={
                metricDeltas.deltas?.savingsRate
                  ? {
                      delta: metricDeltas.deltas.savingsRate,
                      type: "percentage",
                      stagger: 2,
                    }
                  : undefined
              }
            />
            <StatCard
              label="Post-Retirement Income Coverage"
              value={displayPercentage(activeResult.incomeCoverage)}
              icon="🛡️"
              color={activeResult.incomeCoverage >= 1 ? "green" : "teal"}
              deltaInfo={
                metricDeltas.deltas?.incomeCoverage
                  ? {
                      delta: Math.round(
                        metricDeltas.deltas.incomeCoverage * 100
                      ),
                      type: "percentage",
                      stagger: 3,
                    }
                  : undefined
              }
            />
          </div>

          {/* Portfolio Growth Chart — WP-4 Gap 4.1.3: mobile scroll wrapper */}
          <div className="chart-section-mobile">
            <div className="dashboard-section-header">
              <h2>Portfolio Growth Projection</h2>
            </div>
            <div className="chart-mobile-fade">
              <div className="chart-mobile-scroll">
                <div className="chart-mobile-scroll-inner">
                  <Suspense
                    fallback={
                      <div className="chart-placeholder" aria-hidden="true" />
                    }
                  >
                    <PortfolioChart />
                  </Suspense>
                </div>
              </div>
            </div>
          </div>

          {/* What-If Scenario Panel */}
          <WhatIfPanel
            sliderValues={whatIf.sliderValues}
            originalValues={originalSliderValues}
            isActive={whatIf.isActive}
            currentAge={activeData.currentAge}
            onSliderChange={whatIf.setSliderValue}
            onReset={whatIf.reset}
            onSave={handleWhatIfSave}
          />

          {/* Recommendations - Single Column on Mobile */}
          <div ref={recommendationsRef}>
            <div className="dashboard-section-header">
              <h2>Recommendations</h2>
            </div>
            <div className="dashboard-recommendations-stack">
              {generateRecommendations(activeData, activeResult).map(
                (rec, index) => (
                  <RecommendationCard
                    key={index}
                    title={rec.title}
                    description={rec.description}
                    keyActions={rec.keyActions}
                    type={rec.type}
                    onAction={
                      rec.settingsCategory
                        ? () => navigate(`/settings/${rec.settingsCategory}`)
                        : undefined
                    }
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <ProjectionDetailPage
        isOpen={isProjectionPageOpen}
        data={data}
        result={result}
        assetBreakdown={assetBreakdown ?? undefined}
        onClose={() => setIsProjectionPageOpen(false)}
        onOpenSettings={(category) => {
          setIsProjectionPageOpen(false);
          navigate(`/settings/${category}`);
        }}
        useMobileCards={true}
      />

      <UndoToastStack
        toasts={pendingDeletes}
        onUndo={undoDelete}
        onExpire={confirmDelete}
        onDismiss={confirmDelete}
      />

      {comparisonScenarioIds &&
        (() => {
          const currentPreview: ScenarioPreview = {
            retirementAge: activeData.retirementAge,
            finalValue: netWorthAtRetirement ?? activeResult.finalAmount,
            monthlyIncome: activeResult.monthlyIncome,
            readinessScore: activeResult.readinessScore,
            shortTermHealthScore: activeResult.shortTermHealthScore,
            liquidReservesScore: activeResult.liquidReservesScore,
            cashFlowMarginScore: activeResult.cashFlowMarginScore,
            lifestyleScore: activeResult.lifestyleScore,
            currency: activeData.currency,
          };
          const comparisonScenarios = comparisonScenarioIds
            .map((id) => {
              if (id === "__current__") {
                return {
                  id: "__current__",
                  userId: user?.userId ?? "",
                  name: activeScenarioName ?? "Current Plan",
                  description: "",
                  settings: {} as never,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  preview: currentPreview,
                };
              }
              return scenarios.find((s) => s.id === id);
            })
            .filter(
              (s): s is NonNullable<typeof s> & { preview: ScenarioPreview } =>
                s !== undefined && s.preview !== undefined
            );

          if (comparisonScenarios.length < 2) return null;

          return (
            <>
              <div
                className="comparison-backdrop"
                onClick={closeComparison}
                role="presentation"
              />
              <div
                className="comparison-panel"
                role="dialog"
                aria-label="Scenario comparison"
              >
                <ScenarioComparison
                  scenarios={comparisonScenarios}
                  activeScenarioId={
                    comparisonScenarioIds.includes("__current__")
                      ? "__current__"
                      : undefined
                  }
                  onClose={closeComparison}
                  onViewDetails={handleCompareViewDetails}
                />
              </div>
            </>
          );
        })()}
    </div>
  );
}

export default DashboardMobile;
